import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { formatRelative, formatRelativeShort } from "../utils/datetime";
import api from "../api/client";
import { useCustomer } from "../context/CustomerContext";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import { useVisibleColumnCount, Breakpoint } from "../hooks/useVisibleColumns";
import RowActions from "../components/RowActions";
import AIAssistPanel from "../components/AIAssistPanel";
import AIActionReview from "../components/AIActionReview";
import { usePublishScreenData } from "../utils/aiScreenContext";
import { useAutoRefresh } from "../hooks/useAutoRefresh";

type Alert = {
  id: number;
  severity: string;
  metric: string;
  message?: string | null;
  device_id?: number | null;
  device_hostname?: string | null;
  customer_name?: string | null;
  target?: string | null;
  value?: number | null;
  created_at: string;
  acknowledged_at?: string | null;
  // Set once a ticket has been raised from this alert. Drives the row action:
  // with a ticket, the menu OPENS it; without one, it offers to raise it.
  ticket_id?: number | null;
};

const SEVERITIES = [
  { key: "", label: "All" },
  { key: "critical", label: "Critical" },
  { key: "warning", label: "Warning" },
  { key: "info", label: "Info" },
];

const SEV_COLOR: Record<string, string> = {
  critical: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  warning: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  info: "bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
};

// Semantic severity order (crit > warn > info) for click-to-sort.
const SEV_RANK: Record<string, number> = { critical: 0, warning: 1, info: 2 };

/**
 * The one-line "what is wrong" for the phone sub-line under the device name.
 *
 * This gets ~160px before truncation, so it prefers the human-readable
 * message and only falls back to the raw metric key. Two things it strips,
 * both measured off real rows:
 *
 *  - a leading hostname, because the message usually opens with it
 *    ("JasonM-PC battery health is 62%") and that is the line immediately
 *    above;
 *  - the metric key as a prefix, which pushed the readable part off the end.
 *
 * The Metric column still shows the exact key from lg up.
 */
/** Collapse a multi-line agent message onto one line.
 *
 *  Alert text is whatever the agent produced, which for a failed service
 *  start is a PowerShell error block: newlines, a row of tildes, and a
 *  CategoryInfo stack line. Rendered verbatim in a table cell that sets the
 *  height for every other row. Nothing is lost - the full text is still the
 *  tooltip - but the table stays a table.
 */
function oneLine(s?: string | null): string {
  return (s || "").replace(/\s+/g, " ").trim();
}

function alertDetail(a: Alert): string {
  const metric = a.metric ? String(a.metric) : "";
  let detail = a.message || (a.value != null ? String(a.value) : "");
  const host = a.device_hostname || "";
  if (host && detail.toLowerCase().startsWith(host.toLowerCase())) {
    detail = detail.slice(host.length).replace(/^[\s:,\u2014-]+/, "");
  }
  return detail || metric || "—";
}

export default function Alerts() {
  const [params, setParams] = useSearchParams();
  // Declared with the other hooks and ABOVE every early return — a hook below
  // one is a rules-of-hooks build error, and CI=true turns that into a failed
  // build, which empties build/ while Caddy is serving it.
  const navigate = useNavigate();
  const severity = params.get("severity") || "";
  // ?ack=1 shows the acknowledged-but-open backlog. Acking hides an alert from
  // the active feed, so without this view those alerts were unreachable — 154
  // acknowledged criticals existed with no screen that would list them.
  const ackView = params.get("ack") === "1";
  /* The customer switcher scopes the LIST, so it must scope the bulk actions.
   *
   * REPORTED: "the clear all / remediate all buttons aren't honoring the
   * customer selector and want to apply to all alerts for all customers even
   * if a single customer alerts are on the screen."
   *
   * Why it happened: api/client.ts injects customer_id globally, but only on
   * GET. Every POST - including these two - was therefore unscoped, so the
   * button cleared far more than the screen was showing. The endpoints have
   * always accepted customer_id; nothing was ever sending it.
   */
  const { currentCustomerId, current } = useCustomer();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  // Which alert has its AI diagnosis open. One at a time — several open at
  // once is a wall of text, and each costs an AI call against the ceiling.
  const [diagnosing, setDiagnosing] = useState<number | null>(null);
  const { sort, toggle } = useSort("created_at", "desc");

  // `quiet` skips the loading state. A background refresh must not flash a
  // spinner over a table someone is reading every thirty seconds — the point
  // is that new alerts appear, not that the screen keeps announcing itself.
  const load = (quiet = false) => {
    if (!quiet) setLoading(true);
    setErr("");
    // Hide acknowledged alerts — acking "clears" an alert from the active feed.
    const p: Record<string, string | number | boolean> = {
      limit: 500,
      include_acknowledged: ackView,
      ...(ackView ? { acknowledged_only: true } : {}),
    };
    if (severity) p.severity = severity;
    api
      .get("/api/v1/alerts", { params: p })
      .then((r) => setAlerts(r.data || []))
      .catch((e) => setErr(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  };
  // Reload whenever the severity filter changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => load(), [severity, ackView]);

  // Alerts had NO auto-refresh at all, which is the wrong screen to leave
  // static: its entire purpose is to tell you something just broke. 30s while
  // the tab is visible, and at once on returning to it.
  useAutoRefresh(() => load(true), 30000);

  const clearOne = async (id: number) => {
    try {
      await api.post(`/api/v1/alerts/${id}/clear`);
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  const clearAll = async () => {
    if (
      !window.confirm(
        `Clear all ${alerts.length} ${ackView ? "acknowledged" : severity || "active"} alert(s) ` +
          `for ${current?.name || "ALL CUSTOMERS"}?

` +
          "Clearing resolves them and removes them from the feed. If a problem is " +
          "still present the monitor will raise a fresh alert.",
      )
    )
      return;
    setBusy(true);
    setErr("");
    try {
      const r = await api.post("/api/v1/alerts/clear-all", {
        ...(severity ? { severity } : {}),
        ...(currentCustomerId ? { customer_id: currentCustomerId } : {}),
        include_acknowledged: ackView,
      });
      setNotice(`Cleared ${r.data?.cleared ?? 0} alert(s).`);
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const ack = async (id: number) => {
    try {
      await api.post(`/api/v1/alerts/${id}/acknowledge`);
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  const ackAll = async () => {
    if (!window.confirm(`Acknowledge all ${severity || "active"} alerts?`)) return;
    setBusy(true);
    setErr("");
    setNotice("");
    try {
      const r = await api.post("/api/v1/alerts/acknowledge-all", severity ? { severity } : {});
      setNotice(`Acknowledged ${r.data.acknowledged} alert(s).`);
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const maintAll = async () => {
    const ids = Array.from(new Set(alerts.map((a) => a.device_id).filter(Boolean))) as number[];
    if (ids.length === 0) return;
    if (!window.confirm(`Put ${ids.length} affected device(s) into maintenance mode?`)) return;
    setBusy(true);
    setErr("");
    setNotice("");
    try {
      const r = await api.post("/api/v1/devices/bulk-action", {
        device_ids: ids,
        action: "maintenance_on",
      });
      setNotice(`${r.data.updated} device(s) placed in maintenance mode.`);
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remediateAll = async () => {
    if (
      !window.confirm(
        `Draft AI remediation for all ${severity || "active"} alerts ` +
          `for ${current?.name || "ALL CUSTOMERS"}?`,
      )
    )
      return;
    setBusy(true);
    setErr("");
    setNotice("");
    try {
      const r = await api.post("/api/v1/alerts/ai-remediate-all", {
        ...(severity ? { severity } : {}),
        ...(currentCustomerId ? { customer_id: currentCustomerId } : {}),
      });
      const ids: number[] = r.data.action_ids || [];
      if (ids.length) {
        // Open the first and keep the rest queued; closing one moves on.
        setQueue(ids.slice(1));
        setReviewing(ids[0]);
        setNotice(
          ids.length > 1
            ? `${ids.length} remediations proposed — reviewing them one at a time.`
            : "",
        );
      } else {
        setNotice(`No automated remediation could be proposed for the ${severity || "active"} alerts.`);
      }
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const maintenance = async (a: Alert) => {
    if (!a.device_id) return;
    if (
      !window.confirm(
        `Put ${a.device_hostname || "this device"} into maintenance mode? All alerting for it is muted until you exit.`,
      )
    )
      return;
    setBusy(true);
    setErr("");
    setNotice("");
    try {
      await api.post(`/api/v1/devices/${a.device_id}/maintenance`, { enabled: true });
      setNotice(`${a.device_hostname || "Device"} placed in maintenance mode.`);
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  // The proposal opens for review HERE. It used to say "review it in AI
  // Actions", which meant leaving the alert you were working on to go and
  // find it in a queue.
  const [reviewing, setReviewing] = useState<number | null>(null);
  // Bulk leaves a queue to work through. Reviewing them one after another here
  // beats being told a count and sent to find them.
  const [queue, setQueue] = useState<number[]>([]);

  const remediate = async (a: Alert) => {
    setBusy(true);
    setErr("");
    setNotice("");
    try {
      const r = await api.post(`/api/v1/alerts/${a.id}/ai-remediate`);
      if (r.data.action_id) {
        setReviewing(r.data.action_id);
      } else {
        // Nothing could be proposed — say so rather than opening an empty panel.
        setNotice(r.data.message || "No automated remediation is available for this alert.");
      }
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  /** Raise a ticket from this alert, or open the one already raised.
   *
   * Reported as missing entirely: the only alert-to-ticket path was the AI
   * auto-draft, which needs AI configured and its feature flag on. This is the
   * plain one — the alert's own facts become the ticket, no model involved.
   *
   * The endpoint is idempotent, so a double-click or two technicians on the
   * same row cannot produce two tickets for one alert.
   */
  const createTicket = async (a: Alert) => {
    if (a.ticket_id) {
      navigate(`/tickets/${a.ticket_id}`);
      return;
    }
    setBusy(true);
    setErr("");
    setNotice("");
    try {
      const r = await api.post(`/api/v1/alerts/${a.id}/ticket`);
      const tid = r.data?.ticket_id;
      if (tid) {
        navigate(`/tickets/${tid}`);
      } else {
        setErr("The ticket was not created.");
      }
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  usePublishScreenData(
    "Active Alerts",
    alerts.map((a) => ({
      id: a.id,
      severity: a.severity,
      device: a.device_hostname,
      customer: a.customer_name,
      metric: a.metric,
      detail: a.message,
      acknowledged: !!a.acknowledged_at,
    })),
    severity ? `filtered to ${severity}` : "all open alerts",
    100,
    // The controls this screen really has. Keep in step with the buttons and
    // the RowActions list below — this is what the assistant is allowed to
    // name, and it told a technician to use a "Create ticket from alert" item
    // that did not exist until today.
    [
      "Row menu: Create ticket (or Open ticket #N if one was already raised)",
      "Row menu: Remediate (AI)",
      "Row menu: Diagnose",
      "Row menu: Maintenance (only on alerts that have a device)",
      "Row button: Acknowledge",
      "Row button: Clear",
      "Toolbar: Clear all",
      "Toolbar: Remediate (AI) all",
      "Toolbar: Maintenance all",
      "Toolbar: Acknowledge all",
      "Filter tabs: All / Critical / Warning / Info / Ack'd",
    ],
  );

  const setSeverity = (key: string) => {
    const next = new URLSearchParams(params);
    if (key) next.set("severity", key);
    else next.delete("severity");
    setParams(next);
  };

  // colSpan is an ATTRIBUTE, so no media query reaches it. On Devices a
  // hardcoded colSpan held the table at its full width even with the spanned
  // columns hidden, which defeated the whole exercise. Order and breakpoints
  // MUST mirror the SortableTh list.
  const ALERT_COLUMNS: Breakpoint[] = [
    "base", // Severity
    "base", // Device
    "md",   // Customer
    "lg",   // Metric
    "lg",   // Detail
    "sm",   // When
    "base", // actions
  ];
  const alertCols = useVisibleColumnCount(ALERT_COLUMNS);

  const sorted = sortRows(alerts, sort, (a, key) => {
    switch (key) {
      case "severity":
        return SEV_RANK[a.severity] ?? 99;
      case "device_hostname":
        return (a.device_hostname || `#${a.device_id}`).toLowerCase();
      case "customer_name":
        return (a.customer_name || "").toLowerCase();
      case "metric":
        return (a.metric || "").toLowerCase();
      case "message":
        return (a.message || "").toLowerCase();
      case "created_at":
        return a.created_at ? Date.parse(a.created_at) : 0;
      default:
        return 0;
    }
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Active Alerts</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Open (unresolved) alerts across all monitored devices.
        </p>
      </div>

      {err && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-4 py-2 rounded-lg text-sm">
          {err}
        </div>
      )}
      {notice && (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300 px-4 py-2 rounded-lg text-sm">
          {notice}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {SEVERITIES.map((s) => (
          <button
            key={s.key}
            onClick={() => setSeverity(s.key)}
            className={`px-3 py-1.5 rounded-lg text-sm border ${
              severity === s.key
                ? "bg-brand text-white border-brand"
                : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            }`}
          >
            {s.label}
          </button>
        ))}
        <button
          onClick={() => {
            const next = new URLSearchParams(params);
            if (ackView) next.delete("ack");
            else next.set("ack", "1");
            setParams(next);
          }}
          className={`px-3 py-1.5 rounded-lg text-sm border ${
            ackView
              ? "bg-gray-600 text-white border-gray-600"
              : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          }`}
          title="Show alerts that were acknowledged but haven't resolved yet"
        >
          Ack'd
        </button>
        <button
          onClick={clearAll}
          disabled={busy || alerts.length === 0}
          className="ml-auto px-3 py-1.5 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 rounded-lg text-sm disabled:opacity-50"
          title="Resolve and remove every alert in this view"
        >
          Clear all
        </button>
        <button
          onClick={remediateAll}
          disabled={busy || alerts.length === 0}
          className="px-3 py-1.5 border border-violet-300 dark:border-violet-700 text-violet-700 dark:text-violet-300 rounded-lg text-sm disabled:opacity-50"
        >
          Remediate (AI) all
        </button>
        <button
          onClick={maintAll}
          disabled={busy || alerts.length === 0}
          className="px-3 py-1.5 border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 rounded-lg text-sm disabled:opacity-50"
        >
          Maintenance all
        </button>
        <button
          onClick={ackAll}
          disabled={busy || alerts.length === 0}
          className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm disabled:opacity-50"
        >
          Acknowledge all
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
        {loading ? (
          <div className="p-6 text-sm text-gray-400 dark:text-gray-500">Loading…</div>
        ) : alerts.length === 0 ? (
          <div className="p-6 text-sm text-gray-500 dark:text-gray-400">
            No {ackView ? "acknowledged" : severity || "active"} alerts right now.
          </div>
        ) : (
          <table
            /* w-full, NOT min-w-full.
             *
             * min-w-full is a FLOOR: it lets the table grow past its
             * container, and one cell asking for w-full then made it do
             * exactly that. Screenshotted at 1366px: the Detail column ran
             * off the right edge and took the When column and every action
             * button with it - off-screen, unreachable, with the horizontal
             * scrollbar below the fold as the only hint. w-full makes the
             * container the CEILING, so columns shrink instead of the table
             * growing. */
            className="w-full table-fixed text-sm"
          >
            <thead className="bg-gray-50 dark:bg-gray-900 text-xs uppercase text-gray-500 dark:text-gray-400">
              <tr>
                {/* `hide` must match the matching <td> below EXACTLY - a
                    header hidden while its cell is not shifts every later
                    column and shows values under the wrong headings. */}
                <SortableTh label="Severity" sortKey="severity" sort={sort} onSort={toggle} width="w-[5.5rem]" />
                <SortableTh label="Device" sortKey="device_hostname" sort={sort} onSort={toggle} width="w-[9rem]" />
                <SortableTh label="Customer" sortKey="customer_name" sort={sort} onSort={toggle} hide="hidden md:table-cell" width="w-[9.5rem]" />
                <SortableTh label="Metric" sortKey="metric" sort={sort} onSort={toggle} hide="hidden lg:table-cell" width="w-[10rem]" />
                <SortableTh label="Detail" sortKey="message" sort={sort} onSort={toggle} hide="hidden lg:table-cell" />
                <SortableTh label="When" sortKey="created_at" sort={sort} onSort={toggle} hide="hidden sm:table-cell" width="w-[5rem]" />
                <th className="px-3 py-2 w-[3.5rem] xl:w-[12rem]"></th>  {/* kebab only below xl; see the actions cell */}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sorted.map((a) => (
                <React.Fragment key={a.id}>
                <tr>
                  <td className="px-3 py-2">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        SEV_COLOR[a.severity] ||
                        "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {a.severity}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {a.device_id ? (
                      <Link to={`/devices/${a.device_id}`} className="text-brand dark:text-brand-light hover:underline">
                        {a.device_hostname || `#${a.device_id}`}
                      </Link>
                    ) : (
                      a.device_hostname || "—"
                    )}
                    {/* Metric/Detail are hidden until lg and When until sm, so a
                        phone row would name a device without saying what is wrong.
                        Each sub-line vanishes at the breakpoint where its real
                        column comes back, so nothing is ever shown twice. */}
                    <div
                      className="lg:hidden text-xs text-gray-500 dark:text-gray-400 max-w-[10rem] truncate"
                      title={alertDetail(a)}
                    >
                      {alertDetail(a)}
                    </div>
                    {a.created_at ? (
                      <div className="sm:hidden text-[11px] text-gray-400 dark:text-gray-500">
                        {formatRelative(a.created_at)}
                      </div>
                    ) : null}
                  </td>
                  <td
                    className="px-3 py-2 hidden md:table-cell max-w-[12rem] truncate"
                    title={a.customer_name || undefined}
                  >
                    {a.customer_name || "—"}
                  </td>
                  <td className="px-3 py-2 hidden lg:table-cell">
                    {a.metric}
                    {a.target ? (
                      <span className="text-gray-400 dark:text-gray-500"> ({a.target})</span>
                    ) : null}
                  </td>
                  {/* CLAMPED, and whitespace-collapsed before it gets here.
                    *
                    * Alert messages carry raw agent stderr. One row on the
                    * live appliance was seven lines of PowerShell error,
                    * complete with a "~~~~~~~~" rule and a CategoryInfo
                    * stack line: it set the height of the whole table and
                    * still ran off the right edge. The MOBILE rendering of
                    * this same field has always truncated - only the
                    * desktop column was unbounded.
                    *
                    * Two lines then ellipsis, full text on hover, so an
                    * ordinary message still reads in full and a stack trace
                    * cannot take the screen over. */}
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-300 hidden lg:table-cell align-top">
                    <div
                      className="line-clamp-2 leading-snug"
                      title={oneLine(a.message) || undefined}
                    >
                      {oneLine(a.message) ||
                        (a.value != null ? String(a.value) : "—")}
                    </div>
                  </td>
                  {/* Compact + nowrap. The long form overflowed this cell
                      and drew over the first action button. Full text on
                      hover, so nothing is actually lost. */}
                  <td
                    className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 hidden sm:table-cell whitespace-nowrap"
                    title={a.created_at ? formatRelative(a.created_at) : undefined}
                  >
                    {a.created_at ? formatRelativeShort(a.created_at) : "—"}
                  </td>
                  <td className="px-3 py-2">
                    {/* WHY ONLY THREE BUTTONS, AND WHY THE KEBAB IS ALWAYS THERE.
                      *
                      * All five as buttons made this cell 432px of
                      * whitespace-nowrap. On a phone that held the whole table
                      * at 665px inside a 366px container, so every column
                      * hidden at a breakpoint bought nothing - fixed by
                      * dropping to a kebab below md. But the desktop half was
                      * still wrong: at md-lg the five came back while the
                      * viewport was 768-1024px, so with the Detail column
                      * taking the slack the When column and these very buttons
                      * were pushed off the right edge. Measured on a 1366px
                      * laptop, not theorised.
                      *
                      * So the buttons are now a SUBSET chosen for prominence -
                      * the two every alert needs (Acknowledge, Clear) plus
                      * Diagnose - and the kebab is shown at every width
                      * carrying the complete list.
                      *
                      * That also removes the hazard the old note warned about:
                      * there are no longer two lists to keep in step. The kebab
                      * is the full set at all times; the buttons are shortcuts
                      * into it. Adding an action means adding it in one place,
                      * and worst case it is one click further away rather than
                      * missing on half the breakpoints. */}
                    {/* min-w-0 + overflow-hidden: a justify-end flex that
                        outgrows its cell spills LEFTWARDS, and in a
                        table-fixed row that means drawing on top of the
                        previous column. It did - the When value rendered
                        underneath the first button. Clipping here makes the
                        failure visible in this cell instead of corrupting a
                        different one. */}
                    <div className="flex items-center justify-end gap-1.5 min-w-0 overflow-hidden">
                    <div className="hidden xl:flex items-center justify-end gap-1.5 whitespace-nowrap">
                      {a.acknowledged_at ? (
                        <span
                          className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300"
                          title={`Acknowledged ${formatRelative(a.acknowledged_at)}`}
                        >
                          Ack'd
                        </span>
                      ) : (
                        <button
                          onClick={() => ack(a.id)}
                          disabled={busy}
                          className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                        >
                          Acknowledge
                        </button>
                      )}
                      {/* Clear = resolve and remove from the feed, as distinct
                          from acknowledge ("seen, still broken"). If the problem
                          persists the evaluator raises a fresh alert, so nothing
                          is hidden permanently. */}
                      <button
                        onClick={() => clearOne(a.id)}
                        disabled={busy}
                        title="Clear this alert (resolves it and removes it from the feed)"
                        className="text-xs px-2 py-1 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 rounded hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                      >
                        Clear
                      </button>
                    </div>
                      <RowActions
                        busy={busy}
                        actions={[
                          {
                            label: a.ticket_id
                              ? `Open ticket #${a.ticket_id}`
                              : "Create ticket",
                            onClick: () => createTicket(a),
                            disabled: busy,
                            title: a.ticket_id
                              ? "A ticket has already been raised from this alert"
                              : "Raise a ticket pre-filled from this alert",
                          },
                          {
                            label: "Remediate (AI)",
                            onClick: () => remediate(a),
                            disabled: busy,
                            title:
                              "Draft an AI remediation proposal for this alert (review in AI Actions)",
                          },
                          {
                            label: diagnosing === a.id ? "Hide diagnosis" : "Diagnose",
                            onClick: () =>
                              setDiagnosing((cur) => (cur === a.id ? null : a.id)),
                            title:
                              "Explain this alert in plain English and suggest a diagnostic plan",
                          },
                          {
                            label: "Maintenance",
                            onClick: () => maintenance(a),
                            // An alert with no device (a website or certificate
                            // check) has nothing to put into maintenance.
                            hidden: !a.device_id,
                            disabled: busy,
                            title:
                              "Put this device into maintenance mode (mutes its alerts)",
                          },
                          a.acknowledged_at
                            ? {
                                label: "Acknowledged",
                                onClick: () => {},
                                disabled: true,
                                title: `Acknowledged ${formatRelative(a.acknowledged_at)}`,
                              }
                            : {
                                label: "Acknowledge",
                                onClick: () => ack(a.id),
                                disabled: busy,
                                title: "Mark as seen - the alert stays in the feed",
                              },
                          {
                            label: "Clear",
                            onClick: () => clearOne(a.id),
                            danger: true,
                            disabled: busy,
                            title:
                              "Clear this alert (resolves it and removes it from the feed)",
                          },
                        ]}
                      />
                    </div>
                  </td>
                </tr>
                {diagnosing === a.id && (
                  <tr>
                    {/* Expansion row rather than a modal: the technician is
                        working down a list, and a modal would force a close per
                        alert on a page whose whole job is getting through many. */}
                    <td colSpan={alertCols} className="px-3 pb-3 bg-gray-50/70 dark:bg-gray-900/40">
                      <AIAssistPanel alertId={a.id} autoRun onActioned={load} />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {reviewing !== null && (
        <AIActionReview
          actionId={reviewing}
          title="AI remediation"
          onClose={() => {
            // Advance through a bulk queue rather than dropping the rest.
            const [next, ...rest] = queue;
            setQueue(rest);
            setReviewing(next ?? null);
          }}
          // An executed remediation usually clears the alert, so refresh the
          // list underneath rather than leaving a stale row.
          onDone={load}
        />
      )}
    </div>
  );
}
